import React, { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import { SwimlaneHeaders } from "./lib/SwimlaneHeaders";
import { Block, Layer, ProcessGroup, SwimlaneData } from "./lib/types";
import { getBpmnSymbol, getBpmnDropDimensions } from "./data/bpmnSymbols";
import { getDmnSymbol, getDmnDropDimensions } from "./data/dmnSymbols";
import { getVsmSymbol, getVsmDropDimensions } from "./data/vsmSymbols";
import { useExportHandlers } from "./hooks/useExportHandlers";
import { useClipboardActions } from "./hooks/useClipboardActions";
import { FamlaServices } from "../services";
import { parseProcessType } from "../utils/processTypeUtils";
import { LARGE_SWIMLANE_JSON_CHARS } from "./lib/utils/mapSwitchFlags";
import { getParsedSheet } from "./lib/utils/parsedSheetCache";
import { invalidateStakeholderCacheForMap } from "./lib/utils/stakeholderCacheStore";
import {
  beginMapSwitch,
  endMapSwitch,
  markMapSwitchPhase,
} from './lib/utils/mapSwitchPerf';
import { getNotePositionNearTask } from './lib/utils/blockUtils';
import {
  hasContentFitApplied,
  toggleBlocksContentFit,
} from "./lib/utils/blockTextLayout";
import type { MapSwitchGuards } from "./lib/utils/sheetCacheUtils";
import type { SaveStatus } from "./hooks/useCollaboration";
import { useAppSelector } from "../stores";
import { useTranslation } from "react-i18next";
import { getBrandingColor } from "../utils/brandingStyles";
import {
  ApprovalModal,
  ConfirmationModal,
  GenericModal,
  ShareModal,
} from "../views/modal";
import { Tutorial, FloatingTutorialButton } from "../components";
import { useTutorial } from "../hooks/useTutorial";
import { useSampleProcess } from "../hooks/useSampleProcess";
import {
  flowchartTutorialSteps,
  sampleProcessTutorialSteps,
} from "../config/tutorialSteps";
import { LayerSettings } from "./components/PopUp/layerSetting";
import { CollaboratorModal } from "./components/PopUp/CollaboratorModal";
import { AttributModal } from "./components/PopUp/AttributModal";
// import { LeftSidebar } from './components/Layout/LeftSidebar';
import TopBar from "./components/Layout/TopBar";
import { ShapeLibrarySidebar } from "./components/Layout/ShapeLibrarySidebar";
import { iUser } from "../components/type";
import { generateLayerSelections } from "../../src/swimlane/data/layer";
// import { Sidebar } from './components/Layout/Sidebar';
// import { initialSwimlaneData, initialBlocks, initialProcessGroups } from "../swimlane/data/swimlaneData"
import { AnalysisPanel } from "./components/AnalysisPanel/AnalysisPanel";
import { RightSidebar } from "./components/Layout/RightSidebar";
import ResizableLayout from "./components/Layout/ResizableLayout";
import { SwimlaneTabs } from "./components/Layout/SwimlaneTabs";
import { MapSwitchOverlay } from "./components/Layout/MapSwitchOverlay";
import { AttributesPayload } from "./components/PopUp/AttributModal";
import { shouldDeferHeavyMapWork } from "./lib/utils/renderOptimization";
import { useCollaboration, ActiveUser } from "./hooks/useCollaboration";
import JoinNotification from "./components/Layout/JoinNotification";
import CollaborativeCursorsOverlay from "./components/Layout/CollaborativeCursorsOverlay";

interface childprops {
  AnalyseId: string;
  swimlaneData: any;
  processId: string;
  allSwimLane: any[];
  loadAllSwimlane: () => void;
  sId: string;
  canEditMap: boolean;
  /** Porteur ou co-propriétaire (API), pour le même accès qu’en « Propriétaire » en cartographie */
  isProcessMapManager?: boolean;
  approvallist: any;
  setMap: React.Dispatch<React.SetStateAction<string>>;
  layer: any;
  handleSelectionsChange: () => void;
  collaborators: any[];
  setCollaborators: React.Dispatch<React.SetStateAction<any[]>>;
  processesWithDiagrams: any[];
  onPermissionGranted?: () => void;
  switchGuardsRef?: React.MutableRefObject<MapSwitchGuards>;
  isMapSwitching?: boolean;
  mapSwitchTargetId?: string | null;
  mapGenPending?: boolean;
  onMapSwitchRenderComplete?: () => void;
  onInitialRenderComplete?: () => void;
  onSheetCacheUpdate?: (
    sheetId: string,
    patch: Record<string, unknown>,
  ) => void;
}

function App(props: childprops) {
  const mapId = useAppSelector((state) => state.mapId.mapId);
  const users = useAppSelector((state) => state.auth);

  const {
    AnalyseId,
    swimlaneData,
    processId,
    allSwimLane,
    loadAllSwimlane,
    sId,
    canEditMap,
    isProcessMapManager: isProcessMapManagerProp,
    approvallist,
    setMap,
    layer,
    handleSelectionsChange,
    collaborators,
    setCollaborators,
    processesWithDiagrams,
    onPermissionGranted,
    switchGuardsRef,
    isMapSwitching = false,
    mapSwitchTargetId = null,
    mapGenPending = false,
    onMapSwitchRenderComplete,
    onInitialRenderComplete,
    onSheetCacheUpdate,
  } = props;

  const requestSheetSwitch = useCallback(
    (sheetId: string, options?: { forceSlowPath?: boolean }) => {
      if (options?.forceSlowPath && switchGuardsRef?.current) {
        switchGuardsRef.current.forceSlowPath = true;
      }
      setMap(sheetId);
    },
    [setMap, switchGuardsRef],
  );

  const owner: iUser = (swimlaneData?.process?.owner ?? users.user) as iUser;
  const allswimlaneReverse = React.useMemo(() => {
    return [...allSwimLane].reverse();
  }, [allSwimLane]);
  const sheetOrderStorageKey = React.useMemo(
    () => `swimlane-sheet-order-${processId}`,
    [processId],
  );
  const deletedSheetStorageKey = React.useMemo(
    () => `swimlane-deleted-sheet-ids-${processId}`,
    [processId],
  );
  const [sheetOrder, setSheetOrder] = useState<string[]>([]);
  const [removedSheetIds, setRemovedSheetIds] = useState<string[]>([]);
  const orderedSwimlanes = React.useMemo(() => {
    if (!sheetOrder.length) return allswimlaneReverse;

    const byId = new Map(
      allswimlaneReverse.map((sheet: any) => [sheet?._id, sheet]),
    );
    const ordered = sheetOrder.map((id) => byId.get(id)).filter(Boolean);
    const missing = allswimlaneReverse.filter(
      (sheet: any) => !sheetOrder.includes(sheet?._id),
    );
    return [...ordered, ...missing];
  }, [allswimlaneReverse, sheetOrder]);
  const visibleSwimlanes = React.useMemo(
    () =>
      orderedSwimlanes.filter(
        (sheet: any) => !removedSheetIds.includes(sheet?._id),
      ),
    [orderedSwimlanes, removedSheetIds],
  );

  const mapSwitchTargetSheet = React.useMemo(() => {
    if (!mapSwitchTargetId) return null;
    return (
      visibleSwimlanes.find((sheet: any) => sheet?._id === mapSwitchTargetId) ||
      allSwimLane.find(
        (sheet: any) => (sheet?._id || sheet?.id) === mapSwitchTargetId,
      ) ||
      null
    );
  }, [mapSwitchTargetId, visibleSwimlanes, allSwimLane]);

  const mapSwitchTargetName =
    mapSwitchTargetSheet?.name ||
    mapSwitchTargetSheet?._id ||
    mapSwitchTargetId ||
    "";

  // [SAMPLE PROCESS] Détecter si on est dans le processus Employee Onboarding
  const { hasSampleProcess, processId: sampleProcessId } = useSampleProcess();
  const processNameLc = (
    swimlaneData?.process?.name ||
    swimlaneData?.process?.title ||
    ""
  ).toLowerCase();
  const backendIsSample = Boolean(swimlaneData?.process?.is_sample === true);
  const nameLooksSample =
    processNameLc.includes("employee") && processNameLc.includes("onboarding");
  const isSampleProcess =
    (hasSampleProcess && sampleProcessId === processId) ||
    backendIsSample ||
    nameLooksSample;

  const [hiddenSheetIds, setHiddenSheetIds] = useState<string[]>([]);
  const hiddenStorageKey = React.useMemo(
    () => `swimlane-hidden-sheet-ids-${processId}`,
    [processId],
  );

  const sheetPayloadKey = React.useMemo(() => {
    const sheetId = String((swimlaneData as any)?._id || sId || "");
    const swim =
      typeof swimlaneData?.swimlane === "string" ? swimlaneData.swimlane : "";
    const second =
      typeof swimlaneData?.second === "string" ? swimlaneData.second : "";
    const line =
      typeof swimlaneData?.line === "string" ? swimlaneData.line : "";
    return `${sheetId}|${swim.length}|${second.length}|${
      line.length
    }|${swim.slice(0, 32)}|${line.slice(0, 32)}`;
  }, [swimlaneData, sId]);

  const parsedSheetBundle = React.useMemo(() => {
    const sheetId = String((swimlaneData as any)?._id || sId || "");
    const swim =
      typeof swimlaneData?.swimlane === "string" ? swimlaneData.swimlane : "";
    const second =
      typeof swimlaneData?.second === "string" ? swimlaneData.second : "[]";
    const line =
      typeof swimlaneData?.line === "string" ? swimlaneData.line : "";
    const processDisplayName =
      swimlaneData?.process?.name ||
      swimlaneData?.process?.title ||
      swimlaneData?.processName ||
      swimlaneData?.name ||
      "Swimlane Diagram";

    const cached = getParsedSheet(
      sheetId,
      swim,
      second,
      line,
      processDisplayName,
    );
    if (cached) {
      const swimlaneDataParsed = {
        ...cached.swimlaneData,
        header: {
          ...(cached.swimlaneData.header || {}),
          text: processDisplayName,
        },
      };
      return {
        initialBlocks: cached.blocks,
        initialProcessGroups: cached.groups,
        initialSwimlaneData: swimlaneDataParsed,
      };
    }

    try {
      const initialBlocks = JSON.parse(swim || "[]") as Block[];
      const initialProcessGroups = JSON.parse(second || "[]") as ProcessGroup[];
      const parsedLineData = JSON.parse(line || "{}") as SwimlaneData;
      const initialSwimlaneData: SwimlaneData = {
        ...(parsedLineData || {
          width: 1200,
          height: 600,
          lines: [],
          header: { text: "Swimlane Diagram" },
        }),
        header: {
          ...((parsedLineData && parsedLineData.header) || {}),
          text: processDisplayName,
        },
      };
      return { initialBlocks, initialProcessGroups, initialSwimlaneData };
    } catch {
      return {
        initialBlocks: [] as Block[],
        initialProcessGroups: [] as ProcessGroup[],
        initialSwimlaneData: {
          width: 1200,
          height: 600,
          lines: [],
          header: { text: processDisplayName },
        } as SwimlaneData,
      };
    }
  }, [sheetPayloadKey, swimlaneData, sId]);

  const { initialBlocks, initialProcessGroups, initialSwimlaneData } =
    parsedSheetBundle;

  const processDisplayName =
    swimlaneData?.process?.name ||
    swimlaneData?.process?.title ||
    swimlaneData?.processName ||
    swimlaneData?.name ||
    "Swimlane Diagram";

  const processTypeStr = React.useMemo(
    () => parseProcessType((swimlaneData as any)?.process?.type),
    [swimlaneData],
  );

  const lines = initialSwimlaneData.lines
    ? initialSwimlaneData.lines.map((line: any) => ({
        ...line,
      }))
    : [];

  const swimData = [
    {
      ...initialSwimlaneData,
      lines: undefined,
    },
    ...initialProcessGroups,
    ...initialBlocks,
    ...lines,
  ];

  const baseWidth = initialSwimlaneData.width || 1200;
  const baseHeight = initialSwimlaneData.lines
    ? initialSwimlaneData.lines.length * 200
    : 600;

  // References and states
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const swimlaneRef = useRef<SwimlaneHeaders | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [scale, setScale] = useState(1);
  const [currentData, setCurrentData] = useState(initialSwimlaneData);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [blocks, setBlocks] = useState<Block[]>(initialBlocks);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const mapSwitchPerfIdRef = useRef(0);
  const lastAutoSavePayloadRef = useRef<{
    swimlaneData: SwimlaneData;
    blocks: Block[];
    groups: ProcessGroup[];
  } | null>(null);
  const isPrimaryOwner = Boolean(owner?._id && users.user?._id === owner._id);
  const isMapManager = isPrimaryOwner || isProcessMapManagerProp === true;
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const closeModal = useRef<HTMLButtonElement>(null);
  const renameModalCloseRef = useRef<HTMLSpanElement>(null);
  const renameModalTriggerRef = useRef<HTMLSpanElement>(null);
  const deleteModalTriggerRef = useRef<HTMLSpanElement>(null);
  const deleteModalCloseRef = useRef<HTMLSpanElement>(null);
  const inputRefa = useRef<HTMLInputElement>(null);
  const inputRefdisa = useRef<HTMLInputElement>(null);
  const role = useAppSelector((state) => state.role.role);
  const [showLayers, setShowLayers] = useState(false);
  const [showLayersLine, setShowLayersLine] = useState(false);
  const [showWarnings, setShowWarnings] = useState(true);
  const [showNotes, setShowNotes] = useState(() => {
    try {
      const raw = localStorage.getItem("swimlane-show-notes");
      return raw === null ? true : JSON.parse(raw) !== false;
    } catch {
      return true;
    }
  });
  const warningsEnabledForCurrentMap = !isSampleProcess && showWarnings;
  const warningsStorageKey = React.useMemo(() => {
    const effectiveMapId = mapId || sId || processId || "default";
    return `swimlane-show-warnings-${effectiveMapId}`;
  }, [mapId, sId, processId]);
  const [showRightPanel, setShowRightPanel] = useState(false);
  // const [showLayers, setShowLayers] = useState(() => {
  //   const saved = localStorage.getItem('showLayers');
  //   return saved !== null ? JSON.parse(saved) : true;
  // });
  const [marginTop, setMarginTop] = useState("60px");
  const [title, setTitle] = useState("");
  // const [mappId, setMappId] = useState(() => localStorage.getItem("mapId") || '');
  const layerSelection = generateLayerSelections(t);

  // Tutorial setup - Utiliser le tutoriel sample si on est dans Employee Onboarding
  const tutorialSteps = isSampleProcess
    ? sampleProcessTutorialSteps(t)
    : flowchartTutorialSteps(t);
  const tutorialKey = isSampleProcess ? "sampleProcess" : "flowchart";

  const {
    run: runTutorial,
    stepIndex,
    steps,
    startTutorial,
    handleJoyrideCallback,
    isTutorialCompleted,
    resetTutorialProgress,
  } = useTutorial(tutorialSteps, tutorialKey, false, true); // Manual start only, disable filtering to show all 11 steps

  // Initialize clipboard actions
  const { handleCopy, handleCut, handlePaste } = useClipboardActions(
    blocks,
    setBlocks,
    swimlaneRef,
    selectedBlock,
    setSelectedBlock,
  );

  let lastSwimlaneName = allSwimLane[allSwimLane.length - 1]?.name;

  const [state, setState] = useState({
    submit: false,
    swimlaneName: "",
    comment: "",
    isDisabled: true,
  });

  const [alert, setAlert] = useState({
    type: "",
    msg: "",
  });
  const [pendingSheetAction, setPendingSheetAction] = useState<{
    type: "rename" | "copy";
    sheetId: string;
  } | null>(null);
  const [sheetIdPendingDelete, setSheetIdPendingDelete] = useState<string>("");
  const [sheetNamePendingDelete, setSheetNamePendingDelete] =
    useState<string>("");

  useEffect(() => {
    const raw = localStorage.getItem(sheetOrderStorageKey);
    if (!raw) return;
    try {
      const savedOrder = JSON.parse(raw);
      if (Array.isArray(savedOrder)) {
        setSheetOrder(savedOrder.filter((id) => typeof id === "string"));
      }
    } catch {
      setSheetOrder([]);
    }
  }, [sheetOrderStorageKey]);

  useEffect(() => {
    const raw = localStorage.getItem(deletedSheetStorageKey);
    if (!raw) return;
    try {
      const deletedIds = JSON.parse(raw);
      if (Array.isArray(deletedIds)) {
        setRemovedSheetIds(deletedIds.filter((id) => typeof id === "string"));
      }
    } catch {
      setRemovedSheetIds([]);
    }
  }, [deletedSheetStorageKey]);

  useEffect(() => {
    const currentIds = allswimlaneReverse
      .map((sheet: any) => sheet?._id)
      .filter(Boolean);
    if (!currentIds.length) return;

    setSheetOrder((prev) => {
      const kept = prev.filter((id) => currentIds.includes(id));
      const added = currentIds.filter((id) => !kept.includes(id));
      const next = [...kept, ...added];
      localStorage.setItem(sheetOrderStorageKey, JSON.stringify(next));
      return next;
    });
  }, [allswimlaneReverse, sheetOrderStorageKey]);

  useEffect(() => {
    const currentIds = allswimlaneReverse
      .map((sheet: any) => sheet?._id)
      .filter(Boolean);
    setRemovedSheetIds((prev) => {
      if (!prev.length) return prev;
      // Keep only tombstones that are still returned by backend.
      const next = prev.filter((id) => currentIds.includes(id));
      localStorage.setItem(deletedSheetStorageKey, JSON.stringify(next));
      return next;
    });
  }, [allswimlaneReverse, deletedSheetStorageKey]);

  // Load hidden sheets from localStorage
  useEffect(() => {
    const raw = localStorage.getItem(hiddenStorageKey);
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (Array.isArray(saved)) {
        setHiddenSheetIds(saved.filter((id: any) => typeof id === "string"));
      }
    } catch {
      // ignore
    }
  }, [hiddenStorageKey]);

  // Persist hidden sheets
  useEffect(() => {
    localStorage.setItem(hiddenStorageKey, JSON.stringify(hiddenSheetIds));
  }, [hiddenSheetIds, hiddenStorageKey]);

  useEffect(() => {
    const raw = localStorage.getItem(warningsStorageKey);
    if (raw === null) return;
    try {
      setShowWarnings(JSON.parse(raw) !== false);
    } catch {
      setShowWarnings(true);
    }
  }, [warningsStorageKey]);

  useEffect(() => {
    localStorage.setItem(warningsStorageKey, JSON.stringify(showWarnings));
  }, [warningsStorageKey, showWarnings]);

  useEffect(() => {
    localStorage.setItem("swimlane-show-notes", JSON.stringify(showNotes));
  }, [showNotes]);

  const postMapping = async (name: string, payload: any) => {
    const jsonData = JSON.stringify(payload, null, 2);
    return FamlaServices(
      "api_host",
      `/mapping/new/${processId}?name=${encodeURIComponent(name)}`,
      "PUT",
      jsonData,
      users.access_token,
    );
  };

  const buildBlankPayload = (sheetTitle: string) => {
    const blankData = {
      ...currentData,
      header: {
        ...(currentData?.header || {}),
        text: sheetTitle,
      },
      lines: [],
    };

    const blankGroups = (initialProcessGroups || []).map((group: any) => ({
      ...group,
      groupChildren: [],
    }));

    return {
      swimlaneData: blankData,
      blocks: [],
      groups: blankGroups,
    };
  };

  const handleAddSheet = async () => {
    if (!(isMapManager || canEditMap)) return;
    const baseName = t("features.swimlane.newSheetDefaultName");
    const nextNumber = allSwimLane.length + 1;
    const newSheetName = `${baseName} ${nextNumber}`;

    try {
      const response = await postMapping(
        newSheetName,
        buildBlankPayload(newSheetName),
      );
      if (response.status === 200) {
        const newId = response.body?.data?._id || response.body?.data?.id;
        if (newId) {
          // Conserver un ordre stable local: nouvelle feuille en tête
          setSheetOrder((prev) => {
            const next = [newId, ...prev.filter((id) => id !== newId)];
            localStorage.setItem(sheetOrderStorageKey, JSON.stringify(next));
            return next;
          });
          requestSheetSwitch(newId, { forceSlowPath: true });
        }
        await loadAllSwimlane();
        setAlert({ type: "success", msg: t("features.swimlane.sheetCreated") });
      } else {
        setAlert({ type: "warning", msg: t("navigation.anErrorOcured") });
      }
    } catch {
      setAlert({ type: "warning", msg: t("navigation.anErrorOcured") });
    }
  };

  const prepareRenameForMap = (sheetId: string, openModal: boolean) => {
    const sheet = allSwimLane.find((item: any) => item?._id === sheetId);
    const currentName = (sheet?.name || "").trim();
    setState((prevState) => ({
      ...prevState,
      swimlaneName: currentName,
      isDisabled: currentName.length === 0,
    }));

    if (openModal) {
      // Open the existing bootstrap modal from tab actions.
      setTimeout(() => renameModalTriggerRef.current?.click(), 0);
    }
  };

  const handleRenameSheet = (sheetId: string) => {
    if (!(isMapManager || canEditMap)) return;
    if (!sheetId) return;
    if (sheetId !== mapId) {
      setPendingSheetAction({ type: "rename", sheetId });
      setMap(sheetId);
      return;
    }

    prepareRenameForMap(sheetId, true);
  };

  const handleDeleteSheet = async (sheetId: string): Promise<boolean> => {
    if (!(isMapManager || canEditMap)) return false;
    const targetSheet = allSwimLane.find(
      (sheet: any) => sheet?._id === sheetId,
    );
    const targetName =
      targetSheet?.name || t("features.swimlane.untitledSheet");

    if (allSwimLane.length <= 1) {
      setAlert({
        type: "warning",
        msg: t("features.swimlane.cannotDeleteLastSheetDetailed"),
      });
      return false;
    }

    const endpointsToTry = [
      `/mapping/${sheetId}`,
      `/mapping/delete/${sheetId}`,
    ];

    try {
      let success = false;
      for (const endpoint of endpointsToTry) {
        const response = await FamlaServices(
          "api_host",
          endpoint,
          "DELETE",
          "",
          users.access_token,
        );
        if (response.status === 200 || response.status === 204) {
          success = true;
          break;
        }
      }

      if (!success) {
        setAlert({
          type: "warning",
          msg: t("features.swimlane.sheetDeleteFailed", { name: targetName }),
        });
        return false;
      }

      setSheetOrder((prev) => {
        const next = prev.filter((id) => id !== sheetId);
        localStorage.setItem(sheetOrderStorageKey, JSON.stringify(next));
        return next;
      });
      setHiddenSheetIds((prev) => prev.filter((id) => id !== sheetId));

      setRemovedSheetIds((prev) => {
        const next = prev.includes(sheetId) ? prev : [...prev, sheetId];
        localStorage.setItem(deletedSheetStorageKey, JSON.stringify(next));
        return next;
      });

      // Disparition immédiate et bascule instantanée si on vient de supprimer l'onglet actif
      if (sheetId === mapId) {
        const currentIndex = orderedSwimlanes.findIndex(
          (s: any) => s?._id === sheetId,
        );
        let fallbackId: string | undefined;
        // Essayer voisin de droite, sinon gauche
        if (currentIndex >= 0) {
          const right = orderedSwimlanes[currentIndex + 1]?._id;
          const left = orderedSwimlanes[currentIndex - 1]?._id;
          fallbackId = right || left;
        } else {
          // Sécurité: premier de la liste visible
          fallbackId = orderedSwimlanes.find(
            (s: any) => s?._id !== sheetId,
          )?._id;
        }
        if (fallbackId) requestSheetSwitch(fallbackId, { forceSlowPath: true });
      }

      await loadAllSwimlane();

      // mapId déjà mis à jour juste après suppression si nécessaire
      setAlert({
        type: "success",
        msg: t("features.swimlane.sheetDeletedDetailed", { name: targetName }),
      });
      return true;
    } catch {
      setAlert({
        type: "warning",
        msg: t("features.swimlane.sheetDeleteFailed", { name: targetName }),
      });
      return false;
    }
  };

  const requestDeleteSheet = (sheetId: string) => {
    if (!(isMapManager || canEditMap)) return;
    const targetSheet = allSwimLane.find(
      (sheet: any) => sheet?._id === sheetId,
    );
    setSheetIdPendingDelete(sheetId);
    setSheetNamePendingDelete(
      targetSheet?.name || t("features.swimlane.untitledSheet"),
    );
    setTimeout(() => deleteModalTriggerRef.current?.click(), 0);
  };

  const confirmDeleteSheet = async () => {
    if (!sheetIdPendingDelete) return;
    const success = await handleDeleteSheet(sheetIdPendingDelete);
    if (success) {
      setSheetIdPendingDelete("");
      setSheetNamePendingDelete("");
      deleteModalCloseRef.current?.click();
    }
  };

  const handleCopySheetById = async (sheetId: string) => {
    if (!(isMapManager || canEditMap)) return;
    if (!sheetId) return;
    if (sheetId !== mapId) {
      setPendingSheetAction({ type: "copy", sheetId });
      setMap(sheetId);
      return;
    }

    await handleMakeCopy();
  };

  const handleMoveSheet = (sheetId: string, direction: "left" | "right") => {
    if (!(isMapManager || canEditMap)) return;
    const index = visibleSwimlanes.findIndex(
      (sheet: any) => sheet._id === sheetId,
    );
    if (index < 0) return;
    if (direction === "left" && index === 0) return;
    if (direction === "right" && index === visibleSwimlanes.length - 1) return;

    const targetIndex = direction === "left" ? index - 1 : index + 1;
    const sourceId = visibleSwimlanes[index]?._id;
    const targetId = visibleSwimlanes[targetIndex]?._id;
    if (!sourceId || !targetId) return;

    setSheetOrder((prev) => {
      const next = [...prev];
      const sourcePos = next.indexOf(sourceId);
      const targetPos = next.indexOf(targetId);
      if (sourcePos === -1 || targetPos === -1) return prev;

      [next[sourcePos], next[targetPos]] = [next[targetPos], next[sourcePos]];
      localStorage.setItem(sheetOrderStorageKey, JSON.stringify(next));
      return next;
    });
  };

  const handleToggleSheetVisibility = (sheetId: string) => {
    if (!(isMapManager || canEditMap)) return;
    setHiddenSheetIds((prev) =>
      prev.includes(sheetId)
        ? prev.filter((id) => id !== sheetId)
        : [...prev, sheetId],
    );
  };

  // ── Collaboration temps réel ──────────────────────────────────────────────
  const [userJoinedEvent, setUserJoinedEvent] = useState<ActiveUser | null>(
    null,
  );
  const [userLeftEvent, setUserLeftEvent] = useState<ActiveUser | null>(null);
  // Ref pour dédoublonner les événements user_joined/left (double-mount StrictMode)
  const lastJoinEventRef = useRef<string>("");
  const lastLeftEventRef = useRef<string>("");

  // swimlaneData._id est la source de vérité : le redux mapId peut être
  // périmé (persisté d'une session précédente) et pointer vers une autre map.
  const effectiveMapId = (swimlaneData as any)?._id || sId || "";
  const activeSheetId = effectiveMapId || mapId || "";

  const [readOnlyToast, setReadOnlyToast] = useState(false);
  const [warningsStakeholderLoading, setWarningsStakeholderLoading] =
    useState(false);
  const onStakeholderVerificationLoading = useCallback((loading: boolean) => {
    setWarningsStakeholderLoading(loading);
  }, []);

  // Pas d’indicateur « chargement » si les warnings sont masqués (sample / toggle off) — les /reference/ peuvent continuer en fond.
  useEffect(() => {
    if (!warningsEnabledForCurrentMap) {
      setWarningsStakeholderLoading(false);
    }
  }, [warningsEnabledForCurrentMap]);

  const {
    activeUsers,
    saveStatus,
    isConnected,
    triggerAutoSave,
    broadcastLocalEdit,
    sendCursorMove,
  } = useCollaboration({
    mapId: effectiveMapId,
    userId: users.user?._id || "",
    fullname: users.user?.fullname || "",
    email: users.user?.email || "",
    role: role || "",
    token: users.access_token || "",
    processId,
    onPermissionGranted: useCallback(() => {
      if (onPermissionGranted) onPermissionGranted();
    }, [onPermissionGranted]),
    onMapUpdate: useCallback(
      (editData: any) => {
        // Ne pas appliquer les mises à jour envoyées par soi-même (évite le doublon visuel D3)
        const authorId = editData?.user_id || editData?.modifications?.user_id;
        if (authorId && authorId === users.user?._id) return;
        const mod = editData?.modifications;
        if (!mod?.blocks || !swimlaneRef.current) return;
        swimlaneRef.current.updateData(
          mod.swimlaneData,
          mod.blocks,
          mod.groups,
        );
      },
      [users.user?._id],
    ),
    onUserJoined: useCallback((user: ActiveUser) => {
      // Ignorer si même userId que le dernier événement (double-mount StrictMode)
      if (lastJoinEventRef.current === user.user_id) return;
      lastJoinEventRef.current = user.user_id;
      setUserJoinedEvent(user);
      // Reset pour permettre une prochaine notification du même user
      setTimeout(() => {
        lastJoinEventRef.current = "";
      }, 2000);
    }, []),
    onUserLeft: useCallback((user: ActiveUser) => {
      if (lastLeftEventRef.current === user.user_id) return;
      lastLeftEventRef.current = user.user_id;
      setUserLeftEvent(user);
      setTimeout(() => {
        lastLeftEventRef.current = "";
      }, 2000);
    }, []),
  });

  useEffect(() => {
    if (!switchGuardsRef) return;
    switchGuardsRef.current = {
      hasUnsavedChanges,
      saveStatus: saveStatus as SaveStatus,
      mapGenPending,
      forceSlowPath: false,
    };
  }, [hasUnsavedChanges, saveStatus, mapGenPending, switchGuardsRef]);

  useEffect(() => {
    if (saveStatus !== "saved" || !onSheetCacheUpdate) return;
    const payload = lastAutoSavePayloadRef.current;
    if (!payload || !effectiveMapId) return;
    invalidateStakeholderCacheForMap(effectiveMapId);
    onSheetCacheUpdate(effectiveMapId, {
      swimlane: JSON.stringify(payload.blocks),
      second: JSON.stringify(payload.groups),
      line: JSON.stringify(payload.swimlaneData),
      updated_at: new Date().toISOString(),
    });
  }, [saveStatus, effectiveMapId, onSheetCacheUpdate]);

  const swimlaneRefTogleLayer = useRef<SwimlaneHeaders | null>(null);

  const handleToggleLineLayers = () => {
    if (swimlaneRefTogleLayer.current) {
      swimlaneRefTogleLayer.current.toggleLineLayers();
      setShowLayersLine((prev) => !prev);
    }
  };

  const [editableAttributes, setEditableAttributes] =
    useState<AttributesPayload>({
      block_layers: swimlaneData.block_layers ?? [],
      line_layers: swimlaneData.line_layers ?? [],
      start_end_layers: swimlaneData.start_end_layers ?? [],
    });

  const attributesDataEditable = {
    ...editableAttributes,
    owner: isMapManager,
    processesWithDiagrams: processesWithDiagrams,
  };

  const handleAttributesSaved = (payload: AttributesPayload) => {
    setEditableAttributes(payload);
    swimlaneRef.current?.updateAttributesDataEditable({
      ...payload,
      owner: isMapManager,
      processesWithDiagrams: processesWithDiagrams,
    });
  };
  function getHeaderText(swimlaneData: { header: { text: string } }): void {
    setTitle(processDisplayName || swimlaneData.header.text);
  }

  // Initialize swimlane (wait for sample detection to avoid warning flash)
  const swimlaneInitializedRef = React.useRef(false);
  useEffect(() => {
    if (swimlaneInitializedRef.current) return;

    // Ensure we have enough info to know if it's a sample before first render
    // If it's a sample process, warnings must be disabled from the very first render.
    const effectiveMapId = mapId || sId || "";

    // Start with warnings disabled to avoid any flash; will enable later if applicable
    const initialWarningsEnabled = false;

    const swimlaneInstance = new SwimlaneHeaders(
      "swimlane-container",
      initialSwimlaneData,
      initialBlocks,
      initialProcessGroups,
      t,
      effectiveMapId,
      setBlocks,
      layer.block,
      layer.line,
      attributesDataEditable,
      users.access_token,
      initialWarningsEnabled,
      isSampleProcess,
      processTypeStr,
      (swimlaneData as any)?.stakeholder_preview,
      onStakeholderVerificationLoading,
    );

    swimlaneRefTogleLayer.current = swimlaneInstance;
    swimlaneRef.current = swimlaneInstance;
    swimlaneInstance.setNotesVisible(showNotes);
    swimlaneInitializedRef.current = true;

    let previousBlocksJson = JSON.stringify(blocks);
    let previousSwimlaneJson = JSON.stringify(initialSwimlaneData);

    swimlaneInstance.onSaveCallback(
      (updatedData, updatedBlocks, updatedGroups) => {
        setCurrentData(updatedData);
        setBlocks(updatedBlocks);

        const newBlocksJson = JSON.stringify(updatedBlocks);
        const newSwimlaneJson = JSON.stringify(updatedData);
        const blocksChanged = newBlocksJson !== previousBlocksJson;
        const swimlaneChanged = newSwimlaneJson !== previousSwimlaneJson;
        previousBlocksJson = newBlocksJson;
        previousSwimlaneJson = newSwimlaneJson;

        // Avant : seul un changement de blocs déclenchait l’autosauvegarde — les déplacements
        // de libellés (textOffsetAlongLine, exitLabelDragOffset, etc.) ne persistaient pas côté API.
        if (!blocksChanged && !swimlaneChanged) return;

        setHasUnsavedChanges(true);

        const completePayload = {
          swimlaneData: updatedData,
          blocks: updatedBlocks,
          groups: updatedGroups ?? initialProcessGroups,
        };
        lastAutoSavePayloadRef.current = completePayload;
        triggerAutoSave(completePayload);

        broadcastLocalEdit("update", {
          blocks: updatedBlocks,
          groups: updatedGroups,
          swimlaneData: updatedData,
          user_id: users.user?._id,
        });
      },
    );

    swimlaneInstance.onEditModeChange((editMode) => {
      setIsEditing(editMode);
      if (!editMode) {
        setSelectedBlock(null);
      }
    });

    swimlaneInstance.onHistoryChange((canUndoValue, canRedoValue) => {
      setCanUndo(canUndoValue);
      setCanRedo(canRedoValue);
    });

    swimlaneInstance.onBlockSelect((block) => {
      setSelectedBlock(block);
    });

    if (isMapManager || canEditMap === true) {
      swimlaneInstance.setEditMode(true);
    }

    swimlaneInstance.render();

    getHeaderText(initialSwimlaneData);

    if (AnalyseId) {
      setShowRightPanel(true);
    }

    // Force a first-frame warnings check/render right after initial render.
    // This avoids delaying visual verification; will be re-synced later once sample detection is final.
    requestAnimationFrame(() => {
      const shouldEnable = !isSampleProcess && showWarnings;
      try {
        swimlaneInstance.setWarningsEnabled(shouldEnable);
        if (shouldEnable) {
          swimlaneInstance.forceRenderWarnings();
        }
      } catch {
        /* no-op */
      }
    });

    return () => {
      swimlaneRefTogleLayer.current = null;
      swimlaneRef.current = null;
      swimlaneInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSampleProcess]);

  useEffect(() => {
    if (!swimlaneRef.current) return;
    swimlaneRef.current.setNotesVisible(showNotes);
  }, [showNotes]);

  // Sync warnings only after sample process id is known to avoid any flash
  useEffect(() => {
    if (!swimlaneRef.current) return;
    // Wait until sample process id is available to make a definitive decision
    const canDecideSample = Boolean(sampleProcessId);
    if (!canDecideSample) return;
    const enabled = sampleProcessId !== processId && showWarnings;
    swimlaneRef.current.setWarningsEnabled(enabled);
  }, [sampleProcessId, processId, showWarnings]);

  // Hard guard: never show warnings on sample process; immediately hide if detected
  useEffect(() => {
    if (!swimlaneRef.current) return;
    if (isSampleProcess) {
      swimlaneRef.current.setWarningsEnabled(false);
    }
  }, [isSampleProcess]);

  // ── Synchronisation mode édition ↔ canEditMap en temps réel ─────────────
  // Déclenché quand les droits sont accordés OU révoqués via WebSocket.
  // setEditMode est idempotent : appeler setEditMode(true) quand c'est déjà true
  // est un no-op, ce qui évite le double-toggle au montage initial.
  useEffect(() => {
    if (!swimlaneRef.current) return;
    const shouldEdit = isMapManager || canEditMap;
    swimlaneRef.current.setEditMode(shouldEdit);
  }, [canEditMap, isMapManager]);

  // ── Tracking curseur sur le canvas → broadcast aux participants ──────────
  useEffect(() => {
    const canvas = parentRef.current;
    if (!canvas || !isConnected) return;

    const handleMouseMove = (e: MouseEvent) => {
      // On mesure la position par rapport à containerRef (le canvas D3 scalé),
      // et non parentRef. Cela neutralise le marginTop du toolbar en mode édition,
      // qui décalerait les coordonnées envoyées aux collaborateurs.
      const ref = containerRef.current ?? canvas;
      const rect = ref.getBoundingClientRect();
      // getBoundingClientRect() sur un élément transformé (scale) retourne
      // les dimensions visuelles → on normalise par scale pour obtenir
      // les coordonnées logiques du canvas (indépendantes du zoom).
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      sendCursorMove(x, y);
    };

    // Throttle : on envoie au max 1 message toutes les 50ms (20fps)
    let lastSent = 0;
    const throttledMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastSent < 50) return;
      lastSent = now;
      handleMouseMove(e);
    };

    canvas.addEventListener("mousemove", throttledMove);
    return () => canvas.removeEventListener("mousemove", throttledMove);
  }, [isConnected, scale, sendCursorMove]);

  const handleZoom = (direction: "in" | "out") => {
    const step = 0.05;
    let newScale = scale;

    // Ajuster l'échelle en fonction de la direction
    if (direction === "in") {
      newScale = Math.min(Math.max(scale + step, 0.5), 2);
    } else {
      newScale = Math.min(Math.max(scale - step, 0.5), 2);
    }

    newScale = Math.round(newScale * 20) / 20; // Arrondir à 2 décimales
    setScale(newScale);

    // Ajuster les dimensions dynamiquement pour le parent
    if (parentRef.current) {
      parentRef.current.style.width = `${baseWidth * newScale}px`;
      parentRef.current.style.height = `${baseHeight ?? 0 * newScale}px`;
    }

    // Appliquer le zoom à l'élément containerRef
    if (containerRef.current) {
      containerRef.current.style.transform = `scale(${newScale})`;
      containerRef.current.style.transformOrigin = "top left";
    }
  };

  useEffect(() => {
    const handleWheelZoom = (event: WheelEvent) => {
      if (event.ctrlKey) {
        // Vérifie si "Ctrl" est maintenu
        event.preventDefault(); // Empêche le zoom du navigateur

        if (event.deltaY < 0) {
          handleZoom("in"); // Molette vers le haut → Zoom avant
        } else {
          handleZoom("out"); // Molette vers le bas → Zoom arrière
        }
      }
    };

    window.addEventListener("wheel", handleWheelZoom, { passive: false });

    return () => {
      window.removeEventListener("wheel", handleWheelZoom);
    };
  }, [scale]);

  // Initialiser la taille du parent au premier rendu
  useEffect(() => {
    if (parentRef.current) {
      parentRef.current.style.width = `${baseWidth}px`;
      parentRef.current.style.height = `${baseHeight}px`;
    }
  }, [baseWidth, baseHeight]);

  // Handle keyboard shortcuts for clipboard actions
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only process if in edit mode
      if (!isEditing) return;

      // Check if attribute edit modal is open - allow copy/paste in modal
      const isAttributeModalOpen =
        document.querySelector(".attribute-edit-modal-overlay") !== null;
      const target = event.target as HTMLElement;
      const activeElement = document.activeElement as HTMLElement;

      // Check if user is editing text in an input, textarea, or contentEditable element
      const isEditingText =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest("input") !== null ||
        target.closest("textarea") !== null ||
        target.closest('[contenteditable="true"]') !== null ||
        target.closest(".attribute-edit-modal") !== null ||
        (activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.isContentEditable ||
            activeElement.closest("input") !== null ||
            activeElement.closest("textarea") !== null ||
            activeElement.closest('[contenteditable="true"]') !== null ||
            activeElement.closest(".attribute-edit-modal") !== null));

      // If editing text or modal is open, allow default browser behavior (copy/paste text)
      if (isEditingText || isAttributeModalOpen) {
        return;
      }

      // Check for Ctrl+A (or Cmd+A on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === "a") {
        // Prevent the default browser "Select All" behavior
        event.preventDefault();
        handleSelectAll();
      }

      // Copy: Ctrl+C or Cmd+C
      if ((event.ctrlKey || event.metaKey) && event.key === "c") {
        event.preventDefault();
        handleCopy();
      }

      // Cut: Ctrl+X or Cmd+X
      if ((event.ctrlKey || event.metaKey) && event.key === "x") {
        event.preventDefault();
        handleCut();
      }

      // Paste: Ctrl+V or Cmd+V
      if ((event.ctrlKey || event.metaKey) && event.key === "v") {
        event.preventDefault();
        handlePaste();
      }

      // Create note with N
      if (!event.ctrlKey && !event.metaKey && event.key.toLowerCase() === "n") {
        event.preventDefault();
        createNoteBlock();
      }

      // Delete selected block(s) — Delete or Backspace, same as the trash control
      const isDeleteKey =
        event.key === "Delete" ||
        event.key === "Backspace" ||
        event.key === "Del" ||
        event.code === "Delete" ||
        event.code === "Backspace";

      if (!event.ctrlKey && !event.metaKey && isDeleteKey) {
        event.preventDefault();
        const deleted = swimlaneRef.current?.deleteSelectedBlocks?.() === true;
        if (deleted) {
          setSelectedBlock(null);
        }
      }
    };

    // Add event listener (capture so Delete/Backspace is not treated as browser back)
    document.addEventListener("keydown", handleKeyDown, true);

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    isEditing,
    handleCopy,
    handleCut,
    handlePaste,
    blocks,
    currentData,
  ]);

  /** Dernière charge utile appliquée au canvas — évite de réécraser l’état à chaque re-render parent ou ref d’objet identique. */
  const lastSyncedPayloadRef = useRef<{
    sheetId: string;
    swim: string;
    second: string;
    line: string;
  }>({
    sheetId: String((swimlaneData as any)?._id || sId || ""),
    swim:
      typeof swimlaneData?.swimlane === "string" ? swimlaneData.swimlane : "",
    second: typeof swimlaneData?.second === "string" ? swimlaneData.second : "",
    line: typeof swimlaneData?.line === "string" ? swimlaneData.line : "",
  });

  // Keep SwimlaneHeaders in sync when switching maps/tabs (new data and context)
  useEffect(() => {
    if (!swimlaneRef.current) return;

    const sheetId = String((swimlaneData as any)?._id || sId || "");
    const swim =
      typeof swimlaneData?.swimlane === "string" ? swimlaneData.swimlane : "";
    const second =
      typeof swimlaneData?.second === "string" ? swimlaneData.second : "";
    const line =
      typeof swimlaneData?.line === "string" ? swimlaneData.line : "";

    const prev = lastSyncedPayloadRef.current;
    const isSheetChange = prev.sheetId !== "" && prev.sheetId !== sheetId;
    const payloadUnchanged =
      prev.sheetId === sheetId &&
      prev.swim === swim &&
      prev.second === second &&
      prev.line === line;

    swimlaneRef.current.setStakeholderPreview(
      (swimlaneData as any)?.stakeholder_preview,
    );

    if (payloadUnchanged && prev.sheetId !== "") {
      const enabled = !isSampleProcess && showWarnings;
      requestAnimationFrame(() => {
        swimlaneRef.current?.setWarningsEnabled(enabled);
        requestAnimationFrame(() => {
          swimlaneRef.current?.forceRenderWarnings();
          onMapSwitchRenderComplete?.();
          onInitialRenderComplete?.();
        });
      });
      return;
    }

    let cancelled = false;
    const perfId = isSheetChange ? beginMapSwitch({ mapId: sheetId }) : 0;
    if (isSheetChange) {
      mapSwitchPerfIdRef.current = perfId;
    }

    const applyParsedPayload = () => {
      if (cancelled || !swimlaneRef.current) return;

      const nextProcessDisplayName =
        swimlaneData?.process?.name ||
        swimlaneData?.process?.title ||
        swimlaneData?.processName ||
        swimlaneData?.name ||
        "Swimlane Diagram";

      markMapSwitchPhase("json_parse", perfId || undefined);
      const cached = getParsedSheet(
        sheetId,
        swim,
        second,
        line,
        nextProcessDisplayName,
      );

      // Si le cache est vide ET qu'il n'y a aucune donnée payload, on ne peut pas rendre.
      if (!cached && !swim && !line) {
        if (isSheetChange) {
          onMapSwitchRenderComplete?.();
        }
        return;
      }

      // Fallback sur les données pré-parsées du useMemo (toujours valides même sur cache miss).
      const nextInitialBlocks = cached?.blocks ?? initialBlocks;
      const nextInitialProcessGroups = cached?.groups ?? initialProcessGroups;
      const nextInitialSwimlaneData =
        cached?.swimlaneData ?? initialSwimlaneData;

      const laneHeaders = nextInitialSwimlaneData?.subHeaderRows?.headers || [];
      const linesForContext = nextInitialSwimlaneData?.lines || [];
      const effectiveMapIdForContext = (swimlaneData as any)?._id || sId || "";
      swimlaneRef.current.setMapContext(
        effectiveMapIdForContext,
        users.access_token || "",
        laneHeaders,
        linesForContext,
        isSampleProcess,
        processTypeStr,
      );

      const shouldDeferMapSwitchRender = shouldDeferHeavyMapWork({
        isSampleProcess,
        showWarnings,
        blockCount: nextInitialBlocks.length,
        isMapSwitch: isSheetChange,
      });

      lastSyncedPayloadRef.current = { sheetId, swim, second, line };

      setBlocks(nextInitialBlocks);
      setCurrentData(nextInitialSwimlaneData);
      if (isSheetChange) {
        setSelectedBlock(null);
        setHasUnsavedChanges(false);
      }

      markMapSwitchPhase("update_data", perfId || undefined);
      swimlaneRef.current.updateData(
        nextInitialSwimlaneData,
        nextInitialBlocks,
        nextInitialProcessGroups,
        {
          resetHistory: isSheetChange,
          isSheetChange,
        },
      );

      const enabled = !isSampleProcess && showWarnings;
      const finishMapSwitchUi = () => {
        if (isSheetChange) {
          onMapSwitchRenderComplete?.();
        } else if (onInitialRenderComplete) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              onInitialRenderComplete?.();
            });
          });
        }
      };
      const enableWarnings = () => {
        swimlaneRef.current?.setWarningsEnabled(enabled);
        window.requestAnimationFrame(() => {
          swimlaneRef.current?.forceRenderWarnings();
          if (perfId) {
            endMapSwitch(perfId, "warnings_ready", {
              mapId: sheetId,
              blockCount: nextInitialBlocks.length,
              lineCount: linesForContext.length,
            });
          }
        });
      };

      if (shouldDeferMapSwitchRender) {
        window.setTimeout(() => {
          finishMapSwitchUi();
          enableWarnings();
        }, 80);
      } else {
        requestAnimationFrame(() => {
          finishMapSwitchUi();
          enableWarnings();
        });
      }
    };

    const payloadChars = swim.length + second.length + line.length;
    const shouldDeferParse =
      isSheetChange &&
      (swim.length > LARGE_SWIMLANE_JSON_CHARS ||
        second.length > LARGE_SWIMLANE_JSON_CHARS ||
        line.length > LARGE_SWIMLANE_JSON_CHARS);

    if (shouldDeferParse) {
      if (payloadChars > LARGE_SWIMLANE_JSON_CHARS * 2) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(applyParsedPayload);
          });
        });
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(applyParsedPayload);
        });
      }
    } else {
      applyParsedPayload();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, sId, swimlaneData]);

  // Handle swimlane width change
  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWidth = parseInt(e.target.value, 10);
    if (newWidth >= 800) {
      const updatedData = { ...currentData, width: newWidth };
      setCurrentData(updatedData);
      swimlaneRef.current?.updateData(updatedData);
      setHasUnsavedChanges(true);
    }
  };

  const handleChange = (event: any) => {
    const { type, name, value, checked } = event.target;
    let fieldValue = type === "checkbox" ? checked : value;

    if (type === "number") {
      fieldValue = Number(fieldValue);
    }

    if (name === "swimlane") {
      // const isSameName = lastSwimlaneName === fieldValue;
      const isSameName = fieldValue.length === 0;

      setState({ ...state, swimlaneName: fieldValue, isDisabled: isSameName });
    }

    if (name === "comment") {
      setState({ ...state, comment: fieldValue });
    }
  };

  function activeFocus() {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }
  function activeFocusa() {
    if (inputRefa.current) {
      inputRefa.current.focus();
    }
  }
  function activeFocusdisa() {
    if (inputRefdisa.current) {
      inputRefdisa.current.focus();
    }
  }

  const handleSubmit = async (swimlaneId: string, status: string) => {
    setState({ ...state, submit: true });
    let newForm = {
      validation: status === "approve" ? true : false,
      comment: state.comment,
      updated_at: Date.now(),
    };
    let data = await FamlaServices(
      "api_host",
      `/approval/${swimlaneId}`,
      "PUT",
      newForm,
      users.access_token,
    );

    if (data.status === 200) {
      let messageType =
        status === "approve"
          ? t("features.swimlane.sucessApprov")
          : t("features.swimlane.sucessDisapprov");
      setAlert({ type: "success", msg: messageType });
      setState((prevState) => ({ ...prevState, submit: false, comment: "" }));
      setTimeout(() => {
        setAlert({ type: "danger", msg: "" });
      }, 5000);
    } else if (data.status === 400) {
      setAlert({ type: "danger", msg: data.body.description });
      setState((prevState) => ({ ...prevState, submit: false, comment: "" }));
      setTimeout(() => {
        setAlert({ type: "", msg: "" });
      }, 5000);
    } else if (data.status === 401) {
      setAlert({ type: "danger", msg: data.body.description });
      setState((prevState) => ({ ...prevState, submit: false, comment: "" }));
      setTimeout(() => {
        setAlert({ type: "", msg: "" });
      }, 5000);
    } else {
      setAlert({ type: "danger", msg: data.body.description });
      setState((prevState) => ({ ...prevState, submit: false, comment: "" }));
      setTimeout(() => {
        setAlert({ type: "", msg: "" });
      }, 5000);
    }
  };

  const exportFile = async (type: string) => {
    const mapId = sId;
    if (!mapId) {
      setAlert({
        type: "danger",
        msg: t("errors.noMapId") || "No map ID available",
      });
      return;
    }

    const link = `/mapping/convert/to-${type}?map_id=${mapId}`;

    try {
      const response = await FamlaServices(
        "api_host",
        link,
        "POST",
        null,
        users.access_token,
      );

      if (response.status === 200 && response.body instanceof Blob) {
        const url = window.URL.createObjectURL(response.body);

        const linkElement = document.createElement("a");
        linkElement.href = url;

        if (type === "xes") {
          linkElement.download = "exported-file.xml";
        } else {
          linkElement.download = `exported-file.${type}`;
        }

        document.body.appendChild(linkElement);
        linkElement.click();
        linkElement.remove();
        window.URL.revokeObjectURL(url);

        setAlert({
          type: "success",
          msg:
            t("export.success") ||
            `File exported successfully as ${type.toUpperCase()}`,
        });
      } else {
        // Si la réponse n'est pas un Blob, essayer de lire le message d'erreur
        const errorMessage =
          response.body?.description ||
          response.body?.message ||
          `Failed to export ${type.toUpperCase()} file`;
        setAlert({ type: "danger", msg: errorMessage });
      }
    } catch (error) {
      console.error("Export error:", error);
      setAlert({
        type: "danger",
        msg:
          t("export.error") ||
          `An error occurred while exporting ${type.toUpperCase()} file`,
      });
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const blockLayer: Layer[] = [
    { id: "1", text: t("leadtime"), type: "minute", value: "" },
    { id: "2", text: t("defectrate"), type: "percent", value: "" },
    { id: "3", text: t("itsystem"), type: "text", value: "" },
    { id: "4", text: t("cost"), type: "$", value: "" },
    { id: "5", text: t("carbonfootprint"), type: "tCO2e", value: "" },
    { id: "6", text: t("headcount"), type: "integer", value: "" },
    { id: "7", text: t("irritantsandimprovements"), type: "text", value: "" },
    { id: "8", text: t("risksandcontrols"), type: "text", value: "" },
    { id: "9", text: t("relateddocuments"), type: "link", value: "" },
    { id: "10", text: t("failuremode"), type: "text", value: "" },
    { id: "11", text: t("wbs"), type: "text", value: "" },
    { id: "12", text: t("link"), type: "link", value: "" },
  ];

  const startLayer: Layer[] = [
    { id: "1", text: t("link"), type: "link", value: "" },
  ];

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const bpmnSymbolRaw = e.dataTransfer.getData("bpmn-symbol");
    const dmnSymbolRaw = e.dataTransfer.getData("dmn-symbol");
    const vsmSymbolRaw = e.dataTransfer.getData("vsm-symbol");
    const bpmnDef = bpmnSymbolRaw ? getBpmnSymbol(bpmnSymbolRaw) : undefined;
    const dmnDef = dmnSymbolRaw ? getDmnSymbol(dmnSymbolRaw) : undefined;
    const vsmDef = vsmSymbolRaw ? getVsmSymbol(vsmSymbolRaw) : undefined;
    const symbolDef = bpmnDef ?? dmnDef ?? vsmDef;
    const shapeType = (symbolDef?.blockType ??
      e.dataTransfer.getData("shape-type")) as Block["type"];
    if (!shapeType) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    const getAdditionalProperties = (
      ids: string[],
      layers: Layer[],
    ): { layers: Layer[] } => {
      if (ids === null) return { layers: layers };
      const filteredLayers = layers.filter((layer) => ids?.includes(layer.id));
      return { layers: filteredLayers };
    };

    const additionalProperties =
      shapeType === "process" || shapeType === "decision"
        ? getAdditionalProperties(layer.block, blockLayer)
        : shapeType === "start" || shapeType === "end"
        ? getAdditionalProperties(layer.startEnd, startLayer)
        : { layers: [] };

    const dropDims = bpmnDef
      ? getBpmnDropDimensions(bpmnDef)
      : dmnDef
        ? getDmnDropDimensions(dmnDef.id)
        : vsmDef
          ? getVsmDropDimensions(vsmDef.id)
          : null;
    const selectedTarget =
      selectedBlock && selectedBlock.type !== "note" ? selectedBlock : null;
    const isTextAnnotation =
      bpmnDef?.id === "textAnnotation" || dmnDef?.id === "textAnnotation";
    const linkToSelected =
      !!selectedTarget &&
      (isTextAnnotation || (shapeType === "note" && !symbolDef));

    // When dropping a text annotation with a symbol selected, place it on a diagonal.
    let dropX = x;
    let dropY = y;
    if (isTextAnnotation && selectedTarget) {
      const existingCount = blocks.filter(
        (item) =>
          item.type === "note" &&
          (item.bpmnSymbol === "textAnnotation" || item.dmnSymbol === "textAnnotation") &&
          item.linkedTaskId === selectedTarget.id,
      ).length;
      const sourceW = selectedTarget.width || 120;
      const sourceH = selectedTarget.height || 60;
      const step = 36;
      dropX = (selectedTarget.x || 0) + sourceW + 48 + existingCount * step;
      dropY = (selectedTarget.y || 0) + sourceH + 28 + existingCount * step;
    }

    const newBlock: Block = {
      id: `block-${Date.now()}`,
      type: shapeType,
      text: symbolDef?.defaultText ?? `New ${shapeType}`,
      x: dropX,
      y: dropY,
      ...(dropDims ? { width: dropDims.width, height: dropDims.height } : {}),
      ...(bpmnDef ? { bpmnSymbol: bpmnDef.id } : {}),
      ...(dmnDef ? { dmnSymbol: dmnDef.id } : {}),
      ...(vsmDef ? { vsmSymbol: vsmDef.id } : {}),
      fill: shapeType === "note" && !symbolDef ? "#fde68a" : undefined,
      linkedTaskId: linkToSelected ? selectedTarget!.id : undefined,
      // Marks palette drops so Verify Info can explain there is no AI/upload source.
      meta: { source: "manual" },
      ...additionalProperties,
    };

    setBlocks((prev) => [...prev, newBlock]);
    swimlaneRef.current?.addBlocks([newBlock]);

    if (isTextAnnotation && linkToSelected && selectedTarget) {
      swimlaneRef.current?.addAssociation?.(selectedTarget.id, newBlock.id);
    }
  };

  const createNoteBlock = () => {
    const container = containerRef.current;
    if (!container) return;

    const selectedTask = selectedBlock && selectedBlock.type !== 'note' ? selectedBlock : null;
    const notePos = selectedTask ? getNotePositionNearTask(selectedTask) : { x: 120, y: 120 };
    const x = notePos.x;
    const y = notePos.y;

    const noteBlock: Block = {
      id: `note-${Date.now()}`,
      type: "note",
      text: "New note",
      x,
      y,
      fill: "#fde68a",
      layers: [],
      linkedTaskId: selectedTask?.id,
    };

    setBlocks((prev) => [...prev, noteBlock]);
    swimlaneRef.current?.addBlocks([noteBlock]);
  };

  const exportWarningsRestoreRef = useRef(!isSampleProcess && showWarnings);

  const prepareDiagramForExport = useCallback(async () => {
    exportWarningsRestoreRef.current = !isSampleProcess && showWarnings;
    swimlaneRef.current?.prepareForExport();
    if (containerRef.current?.parentNode) {
      const container = d3.select(containerRef.current.parentNode as HTMLElement);
      container
        .selectAll('.block-selected')
        .classed('block-selected', false)
        .style('box-shadow', 'none')
        .style('outline', 'none');
      container.selectAll('[class*="-block-container-"]').style('box-shadow', 'none');
    }
    setSelectedBlock(null);
    setBlocks((prev) => prev.map((block) => ({ ...block, selected: false })));

    if (!swimlaneRef.current) return;
    const shouldShow = !isSampleProcess && showWarnings;
    swimlaneRef.current.setWarningsEnabled(shouldShow);
    swimlaneRef.current.forceRenderWarnings();
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    await new Promise<void>((resolve) => setTimeout(resolve, 30));
  }, [isSampleProcess, showWarnings]);

  const restoreWarningsAfterExport = useCallback(async () => {
    swimlaneRef.current?.setWarningsEnabled(exportWarningsRestoreRef.current);
  }, []);

  const { isExporting, handleExportJson, handleExportPng, handleExportPdf } =
    useExportHandlers(containerRef, swimData, {
      beforeExport: prepareDiagramForExport,
      afterExport: restoreWarningsAfterExport,
    });

  // Alias conservés pour compatibilité (menus / hot-reload)
  const handleExportPdfWithWarnings = handleExportPdf;
  const handleExportPngWithWarnings = handleExportPng;

  const completeData = {
    swimlaneData: currentData,
    blocks: blocks,
    groups: initialProcessGroups,
  };

  const resolveCopiedMapId = async (
    copyName: string,
  ): Promise<string | null> => {
    try {
      const listResponse = await FamlaServices(
        "api_host",
        `/mapping/process/${processId}`,
        "GET",
        "",
        users.access_token,
      );
      if (listResponse.status !== 200) return null;

      const responseData = listResponse.body?.data;
      const allMappings = Array.isArray(responseData)
        ? responseData
        : responseData?.accessible || [];

      const exactMatches = allMappings.filter(
        (item: any) => item?.name === copyName,
      );
      if (exactMatches.length === 0) return null;

      const latestMatch = exactMatches.reduce((latest: any, item: any) => {
        const latestDate = new Date(
          latest?.created_at || latest?.updated_at || 0,
        ).getTime();
        const itemDate = new Date(
          item?.created_at || item?.updated_at || 0,
        ).getTime();
        return itemDate >= latestDate ? item : latest;
      }, exactMatches[0]);

      return latestMatch?._id || null;
    } catch {
      return null;
    }
  };

  const handleMakeCopy = async () => {
    const currentMapName =
      allSwimLane.find((map: any) => map._id === mapId)?.name ||
      state.swimlaneName ||
      title ||
      "Diagram";
    const copyName = `${currentMapName} (${t("features.swimlane.copySuffix")})`;

    try {
      // Utiliser le nouvel endpoint de copie, non-bloquant et sans payload lourd
      const response = await FamlaServices(
        "api_host",
        `/mapping/copy/${mapId}?name=${encodeURIComponent(
          copyName,
        )}&re_enrich=true`,
        "POST",
        "",
        users.access_token,
      );

      if (response.status === 200) {
        await loadAllSwimlane();

        const copiedId = response.body?.data?._id || response.body?.data?.id;

        if (copiedId) {
          setSheetOrder((prev) => {
            const next = [copiedId, ...prev.filter((id) => id !== copiedId)];
            localStorage.setItem(sheetOrderStorageKey, JSON.stringify(next));
            return next;
          });
          requestSheetSwitch(copiedId, { forceSlowPath: true });
        } else {
          // Fallback legacy (devrait rarement arriver)
          const resolved = await resolveCopiedMapId(copyName);
          if (resolved) {
            setSheetOrder((prev) => {
              const next = [resolved, ...prev.filter((id) => id !== resolved)];
              localStorage.setItem(sheetOrderStorageKey, JSON.stringify(next));
              return next;
            });
            requestSheetSwitch(resolved, { forceSlowPath: true });
          }
        }

        setAlert({
          ...alert,
          type: "success",
          msg: t("features.swimlane.copyCreated"),
        });
        setTimeout(() => {
          setAlert({ ...alert, type: "", msg: "" });
        }, 3000);
      } else if (response.status === 400) {
        setAlert({
          ...alert,
          type: "warning",
          msg: t("features.swimlane.mapExist"),
        });
      } else {
        setAlert({
          ...alert,
          type: "warning",
          msg: t("navigation.anErrorOcured"),
        });
      }
    } catch {
      setAlert({
        ...alert,
        type: "warning",
        msg: t("navigation.anErrorOcured"),
      });
    }
  };

  const handleSave = async () => {
    // Create the complete data object

    setState({ ...state, submit: true });

    // Convert to JSON string
    const jsonData = JSON.stringify(completeData, null, 2);

    let data = await FamlaServices(
      "api_host",
      `/mapping/new/${processId}?name=${state.swimlaneName}`,
      "PUT",
      jsonData,
      users.access_token,
    );
    if (data.status === 200) {
      loadAllSwimlane();
      closeModal.current && closeModal.current.click();
      setState({
        ...state,
        submit: false,
        swimlaneName: "",
        isDisabled: false,
      });
      setAlert({
        ...alert,
        type: "success",
        msg: t("features.swimlane.mapSaveWithSucess"),
      });
      setTimeout(() => {
        setAlert({ ...alert, type: "", msg: "" });
      }, 3000);
      setHasUnsavedChanges(false);
    } else if (data.status === 400) {
      setAlert({
        ...alert,
        type: "warning",
        msg: t("features.swimlane.mapExist"),
      });
      setState({ ...state, submit: false });
    } else if (
      data.status === 400 &&
      data.body.response_type === "Invalid Data"
    ) {
      setAlert({
        ...alert,
        type: "warning",
        msg: t("features.swimlane.oopsSomething"),
      });
      setState({ ...state, submit: false });
    } else if (
      data.status === 400 &&
      data.body.response_type === "Invalid Data"
    ) {
      setAlert({
        ...alert,
        type: "warning",
        msg: t("features.swimlane.oopsSomething"),
      });
      setState({ ...state, submit: false });
    } else {
      setAlert({
        ...alert,
        type: "warning",
        msg: t("navigation.anErrorOcured"),
      });
      setState({ ...state, submit: false });
    }
  };

  const handleRenameCurrentMap = async () => {
    const safeName = (state.swimlaneName || "").trim();
    if (!safeName || !mapId) return;

    setState((prevState) => ({ ...prevState, submit: true }));

    const renamePayload = {
      name: safeName,
      updated_at: Date.now(),
    };

    try {
      const data = await FamlaServices(
        "api_host",
        `/mapping/update/${mapId}`,
        "PUT",
        renamePayload,
        users.access_token,
      );

      if (data.status === 200) {
        await loadAllSwimlane();
        renameModalCloseRef.current && renameModalCloseRef.current.click();
        setState((prevState) => ({
          ...prevState,
          submit: false,
          swimlaneName: "",
          isDisabled: true,
        }));
        setAlert({
          ...alert,
          type: "success",
          msg: t("features.swimlane.sheetRenamed"),
        });
      } else if (
        data.status === 400 &&
        data.body?.response_type === "Already exists"
      ) {
        setAlert({
          ...alert,
          type: "warning",
          msg: t("features.swimlane.mapExist"),
        });
        setState((prevState) => ({ ...prevState, submit: false }));
      } else {
        setAlert({
          ...alert,
          type: "warning",
          msg: t("navigation.anErrorOcured"),
        });
        setState((prevState) => ({ ...prevState, submit: false }));
      }
    } catch {
      setAlert({
        ...alert,
        type: "warning",
        msg: t("navigation.anErrorOcured"),
      });
      setState((prevState) => ({ ...prevState, submit: false }));
    }
  };

  useEffect(() => {
    if (!pendingSheetAction) return;
    if (pendingSheetAction.sheetId !== mapId) return;

    if (pendingSheetAction.type === "rename") {
      prepareRenameForMap(mapId, true);
    } else if (pendingSheetAction.type === "copy") {
      handleMakeCopy();
    }

    setPendingSheetAction(null);
  }, [pendingSheetAction, mapId]);

  // Initialize layer visibility on component mount
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const layerDivs = container.querySelectorAll('[class*="-layers-"] > div');

      // Set initial visibility to match the state
      layerDivs.forEach((div) => {
        (div as HTMLElement).style.display = showLayers ? "block" : "none";
      });
    }
  }, [showLayers]);
  // Initialize layer visibility on component mount
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const layerDivs = container.querySelectorAll("line-layers");

      // Set initial visibility to match the state
      layerDivs.forEach((div) => {
        (div as HTMLElement).style.display = showLayersLine ? "block" : "none";
      });
    }
  }, [showLayersLine]);

  const handleToggleLayers = () => {
    setShowLayers((prev: any) => {
      const newValue = !prev;
      // Toggle visibility of all layer divs
      const container = containerRef.current;
      if (container) {
        const layerDivs = container.querySelectorAll(
          '[class*="-layers-"] > div',
        );

        layerDivs.forEach((div) => {
          (div as HTMLElement).style.display = newValue ? "block" : "none";
        });
      }
      return newValue;
    });
  };

  // const handleToggleLayersLine = () => {
  //   setShowLayersLine((prev: boolean) => {
  //     const newValue = !prev;

  //     const lineLayers = document.querySelectorAll('[class*="line-layers-"]');

  //     lineLayers.forEach((element) => {
  //       const el = element as HTMLElement;
  //       // Inverser l'affichage
  //       el.style.display = (el.style.display === 'none') ? 'block' : 'none';
  //     });

  //     return newValue;
  //   });
  // };

  const handleTextStyleUpdate = (property: string, value: any) => {
    if (!selectedBlock) return;

    const updatedBlock = {
      ...selectedBlock,
      textStyle: {
        ...selectedBlock.textStyle,
        [property]: value,
      },
    };

    const updatedBlocks = blocks.map((block) =>
      block.id === selectedBlock.id ? updatedBlock : block,
    );

    setBlocks(updatedBlocks);
    setSelectedBlock(updatedBlock);
    swimlaneRef.current?.updateData(
      undefined,
      updatedBlocks,
      initialProcessGroups,
    );
  };
  const handleBlockUpdate = (updates: Partial<Block>) => {
    if (!selectedBlock) return;

    const updatedBlock = {
      ...selectedBlock,
      ...updates,
    };

    const updatedBlocks = blocks.map((block) =>
      block.id === selectedBlock.id ? updatedBlock : block,
    );

    setBlocks(updatedBlocks);
    setSelectedBlock(updatedBlock);
    swimlaneRef.current?.updateData(
      undefined,
      updatedBlocks,
      initialProcessGroups,
    );
  };

  const applyBlocksUpdate = useCallback(
    (updatedBlocks: Block[]) => {
      setBlocks(updatedBlocks);
      if (selectedBlock) {
        setSelectedBlock(
          updatedBlocks.find((b) => b.id === selectedBlock.id) ?? null,
        );
      }
      swimlaneRef.current?.updateData(
        undefined,
        updatedBlocks,
        initialProcessGroups,
      );
    },
    [initialProcessGroups, selectedBlock],
  );

  const handleToggleBlocksContentFit = useCallback(() => {
    applyBlocksUpdate(toggleBlocksContentFit(blocks));
  }, [applyBlocksUpdate, blocks]);

  const isBlocksContentFitActive = hasContentFitApplied(blocks);

  const handleUndo = () => {
    swimlaneRef.current?.undo();
  };

  // After blocks change (e.g., when navigating between tabs), if warnings are enabled for this map,
  // force badges to render to avoid race conditions and missing badges.
  useEffect(() => {
    const enabled = !isSampleProcess && showWarnings;
    if (!enabled || !swimlaneRef.current) return;
    // Let D3 render settle, then force warnings
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        swimlaneRef.current?.setWarningsEnabled(true);
        swimlaneRef.current?.forceRenderWarnings();
      });
    });
  }, [blocks, isSampleProcess, showWarnings]);

  const handleRedo = () => {
    swimlaneRef.current?.redo();
  };

  const handleSelectAll = () => {
    if (!containerRef.current) return;

    const container = d3.select(containerRef.current.parentNode as HTMLElement);

    // Select all blocks
    container
      .selectAll(".block")
      .classed("block-selected", true)
      .style("box-shadow", "0 0 0 2px rgba(59, 130, 246, 0.5)");

    const updatedBlocks = blocks.map((block) => ({
      ...block,
      selected: true,
    }));

    setBlocks(updatedBlocks);
  };

  const handleDeselectAll = () => {
    if (!containerRef.current) return;

    const container = d3.select(containerRef.current.parentNode as HTMLElement);

    // Deselect all blocks
    container
      .selectAll(".block-selected")
      .classed("block-selected", false)
      .style("box-shadow", "none");

    // Update the blocks state to reflect deselection
    const updatedBlocks = blocks.map((block) => ({
      ...block,
      selected: false,
    }));

    setBlocks(updatedBlocks);
    setSelectedBlock(null);
    // swimlaneRef.current?.updateData(undefined, updatedBlocks, initialProcessGroups);
  };

  // Add click handler to the container for deselection
  useEffect(() => {
    const handleContainerClick = (event: MouseEvent) => {
      // Only process if in edit mode
      if (!isEditing) return;

      const target = event.target as HTMLElement;
      const container = containerRef.current?.parentNode as HTMLElement;

      // Check if the click is directly on the container (not on a block or its children)
      if (
        container &&
        (target === container ||
          (target.closest(".diagram-container") &&
            !target.closest(".block") &&
            !target.closest('[class^="block-selection-menu-"]') &&
            !target.closest(".connection-point")))
      ) {
        handleDeselectAll();
      }
    };

    // Add event listener to the parent container
    const containerParent = containerRef.current?.parentNode as HTMLElement;
    if (containerParent) {
      containerParent.addEventListener("click", handleContainerClick);
    }

    // Clean up event listener on component unmount or when editing mode changes
    return () => {
      if (containerParent) {
        containerParent.removeEventListener("click", handleContainerClick);
      }
    };
  }, [isEditing, blocks]);

  const handleSetMapId = (id: string) => {
    localStorage.setItem("mapId", id);
  };

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+A (or Cmd+A on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === "a") {
        // Prevent the default browser "Select All" behavior
        event.preventDefault();
        handleSelectAll();
      }
    };

    // Add event listener
    document.addEventListener("keydown", handleKeyDown);

    // Clean up event listener on component unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleToggleAccess = async (userId: string) => {
    const currentCollab = collaborators.find((c: any) => c.user._id === userId);
    const currentEdit = currentCollab?.edit ?? false;
    const newEdit = !currentEdit;

    const dataEdit = {
      map_id: effectiveMapId,
      user_id: userId,
      edit: newEdit,
    };
    try {
      const data = await FamlaServices(
        "api_host",
        `/approval/edit`,
        "POST",
        dataEdit,
        users.access_token,
      );
      if (data.status === 200 || data.status === 201) {
        setCollaborators((prevCollaborators: any[]) =>
          prevCollaborators.map((collab) =>
            collab.user._id === userId ? { ...collab, edit: newEdit } : collab,
          ),
        );
      }
    } catch (error) {
      // Erreur silencieuse — l'état local reste inchangé
    }
  };

  const handleGrantAllAccess = () => {
    setCollaborators((prevCollaborators) =>
      prevCollaborators.map((collab) => ({ ...collab, edit: true })),
    );
    // Here you would typically make an API call to update all access
  };

  return (
    <>
      <div>
        <div
          className={`flex flex-col h-screen w-screen overflow-hidden fixed tutorial-flowchart-welcome`}
        >
          {/* Main Content */}
          <div className="flex-1 flex flex-col h-full">
            {/* Top Bar */}
            <div data-tutorial="flowchart-topbar">
              <TopBar
                isEditing={isEditing}
                scale={scale}
                hasUnsavedChanges={hasUnsavedChanges}
                handleZoom={handleZoom}
                handleExportPdf={handleExportPdf}
                handleExportPng={handleExportPng}
                isExporting={isExporting}
                exportFile={exportFile}
                activeFocusdisa={activeFocusdisa}
                activeFocusa={activeFocusa}
                approvallist={approvallist}
                processId={processId}
                swimlaneRef={swimlaneRef}
                canEditMap={canEditMap}
                role={role}
                handleToggleLayers={handleToggleLayers}
                handleToggleLayersLine={handleToggleLineLayers}
                onUndo={handleUndo}
                onRedo={handleRedo}
                showLayers={showLayers}
                showLayersLine={showLayersLine}
                showWarnings={warningsEnabledForCurrentMap}
                showNotes={showNotes}
                onToggleNotes={() => {
                  setShowNotes((prev) => {
                    const next = !prev;
                    if (!next && selectedBlock?.type === "note") {
                      setSelectedBlock(null);
                    }
                    return next;
                  });
                }}
                warningsStakeholderLoading={warningsStakeholderLoading}
                onToggleWarnings={() => {
                  if (isSampleProcess) return;
                  setShowWarnings((prev) => !prev);
                }}
                handleBlockUpdate={handleBlockUpdate}
                handleTextStyleUpdate={handleTextStyleUpdate}
                selectedBlock={selectedBlock}
                currentData={currentData}
                handleWidthChange={handleWidthChange}
                canUndo={canUndo}
                canRedo={canRedo}
                setMarginTop={setMarginTop}
                handleSelectAll={handleSelectAll}
                onToggleBlocksContentFit={handleToggleBlocksContentFit}
                isBlocksContentFitActive={isBlocksContentFitActive}
                title={title}
                handleCopy={handleCopy}
                handleCut={handleCut}
                handlePaste={handlePaste}
                allSwimLane={visibleSwimlanes}
                setMap={setMap}
                owner={owner}
                isProcessMapManager={isMapManager}
                collaborators={collaborators}
                setShowRightPanel={setShowRightPanel}
                showRightPanel={showRightPanel}
                onMakeCopy={handleMakeCopy}
                onRenameCurrentMap={() => {
                  prepareRenameForMap(mapId, false);
                  inputRef.current?.focus();
                }}
                activeUsers={activeUsers}
                currentUserId={users.user?._id || ""}
                saveStatus={saveStatus}
                isWsConnected={isConnected}
              />
            </div>

            {/* Toast "lecture seule" */}
            {readOnlyToast && (
              <div
                className="fixed bottom-16 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg animate-fade-in"
                style={{ pointerEvents: "none" }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-yellow-400"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {t("features.swimlane.readOnlyMap")}
              </div>
            )}

            {/* Conteneur principal : bibliothèque | canvas (style draw.io) */}
            <div
              className="flex-1 mt-14 flex min-h-0 relative"
              data-tutorial="flowchart-canvas"
              style={{
                // Clear the fixed properties toolbar when editing (same offset previously on the diagram alone)
                paddingTop: isEditing && marginTop ? marginTop : undefined,
              }}
            >
              <ShapeLibrarySidebar isEditing={isEditing} />

              <div className="flex-1 min-w-0 bg-[#fafafa] relative">
                {isMapSwitching && (
                  <MapSwitchOverlay
                    sheetName={mapSwitchTargetName || undefined}
                  />
                )}
                {/* Conteneur de défilement avec barres de défilement visibles */}
                <div
                  className="absolute inset-0 p-4"
                  style={{
                    overflowX: "scroll",
                    overflowY: "scroll",
                    scrollbarWidth: "thin",
                    scrollbarColor: `${getBrandingColor()} #e2e8f0`,
                    WebkitOverflowScrolling: "touch",
                    msOverflowStyle: "auto",
                  }}
                >
                  {/* Conteneur du diagramme avec dimensions minimales garanties */}
                  <div
                    className="relative"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => {
                      if (!isMapManager && !canEditMap && !isEditing) {
                        setReadOnlyToast(true);
                        setTimeout(() => setReadOnlyToast(false), 2500);
                      }
                    }}
                    style={{
                      minWidth: `${Math.max(baseWidth, 1200)}px`,
                      minHeight: `${Math.max(baseHeight || 0, 600)}px`,
                      width: "fit-content",
                      height: "fit-content",
                    }}
                    ref={parentRef}
                  >
                    <div
                      id="swimlane-container"
                      ref={containerRef}
                      className="bg-white rounded-lg shadow-sm border border-gray-200/80 inline-block"
                      data-tutorial="flowchart-diagram"
                      style={{
                        transformOrigin: "top left",
                        pointerEvents: isMapSwitching ? "none" : undefined,
                        opacity: isMapSwitching ? 0.45 : 1,
                        transition: "opacity 160ms ease-out",
                      }}
                    />
                    {/* Overlay curseurs collaboratifs */}
                    <CollaborativeCursorsOverlay
                      activeUsers={activeUsers}
                      currentUserId={users.user?._id || ""}
                      containerRef={containerRef}
                      scale={scale}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Barre de navigation inférieure - Positionnée de manière fixe sans chevauchement */}
            <SwimlaneTabs
              swimlanes={visibleSwimlanes}
              mapId={activeSheetId}
              setMap={setMap}
              handleSetMapId={handleSetMapId}
              hasUnsavedChanges={hasUnsavedChanges}
              swimlaneName={state.swimlaneName}
              handleAction={handleSave}
              submit={state.submit}
              alert={alert}
              handleChange={handleChange}
              isDisabled={state?.isDisabled}
              onAddSheet={handleAddSheet}
              onRenameSheet={(sheetId: string) => handleRenameSheet(sheetId)}
              onDeleteSheet={requestDeleteSheet}
              onCopySheet={handleCopySheetById}
              onMoveSheet={handleMoveSheet}
              onToggleSheetVisibility={handleToggleSheetVisibility}
              hiddenSheetIds={hiddenSheetIds}
              canManageSheets={isMapManager || canEditMap}
              isMapSwitching={isMapSwitching}
              mapSwitchTargetId={mapSwitchTargetId}
            />
          </div>
        </div>
        {/* {showRightPanel &&
          <RightSidebar className="">
            <AnalysisPanel
              className=''
              processId={processId}
              mapId={mapId}
              setShoRightPanel={setShowRightPanel}
            />
          </RightSidebar>
        } */}
        {showRightPanel && (
          <ResizableLayout
            processId={processId}
            mapId={mapId}
            setShoRightPanel={setShowRightPanel}
            AnalyseId={AnalyseId}
          />
        )}
      </div>
      <ConfirmationModal
        key={3}
        id="renameSwimlane"
        title={t("words.rename")}
        buttonLabel={t("words.rename")}
        inputLabel={t("navigation.diagramName")}
        inputName="swimlane"
        inputValue={state.swimlaneName}
        inputRef={inputRef}
        handleChange={handleChange}
        handleAction={handleRenameCurrentMap}
        submit={state.submit}
        alert={alert}
        closeModalRef={renameModalCloseRef}
        showInput={true}
        isDisabled={state?.isDisabled}
      />
      <span
        ref={renameModalTriggerRef}
        data-bs-target="#renameSwimlane"
        data-bs-toggle="modal"
      />
      <ConfirmationModal
        key={"deleteSheetModal"}
        id="deleteSheetModal"
        title={t("words.delete")}
        message={t("features.swimlane.deleteSheetConfirmDetailed", {
          name: sheetNamePendingDelete,
        })}
        buttonLabel={t("words.delete")}
        inputLabel=""
        inputName="swimlane"
        inputValue=""
        inputRef={inputRef}
        handleChange={handleChange}
        handleAction={confirmDeleteSheet}
        submit={state.submit}
        alert={alert}
        closeModalRef={deleteModalCloseRef}
        showInput={false}
      />
      <span
        ref={deleteModalTriggerRef}
        data-bs-target="#deleteSheetModal"
        data-bs-toggle="modal"
      />

      <ConfirmationModal
        key={2}
        id="disapprove"
        title={t("words.disApprove")}
        message={t("features.swimlane.disApproveQuestion")}
        buttonLabel={t("words.disApprove")}
        inputLabel={t("words.comments")}
        inputName="comment"
        inputValue={state.comment}
        inputRef={inputRefdisa}
        handleChange={handleChange}
        handleAction={() => handleSubmit(sId, "disapprove")}
        submit={state.submit}
        alert={alert}
        closeModalRef={closeModal}
      />
      <ConfirmationModal
        key={1}
        id="approve"
        title={t("words.approve")}
        message={t("features.swimlane.approveQuestion")}
        buttonLabel={t("words.approve")}
        inputLabel={t("words.comments")}
        inputName="comment"
        inputValue={state.comment}
        inputRef={inputRefa}
        handleChange={handleChange}
        handleAction={() => handleSubmit(sId, "approve")}
        submit={state.submit}
        alert={alert}
        closeModalRef={closeModal}
      />

      <ConfirmationModal
        key={"checkSave"}
        id="checkSave"
        title={t("features.swimlane.saveFileAs")}
        message={t("features.swimlane.doYouwantSaveave")}
        buttonLabel={t("words.saveAs")}
        inputLabel={t("navigation.diagramName")}
        inputName="swimlane"
        inputValue={state.swimlaneName}
        inputRef={inputRef}
        handleChange={handleChange}
        handleAction={handleSave}
        submit={state.submit}
        alert={alert}
        closeModalRef={closeModal}
        showInput={true}
        isDisabled={state?.isDisabled}
        route="chatBot"
      />

      <ConfirmationModal
        key={"checkSaveifBacktoHome"}
        id="checkSaveifBacktoHome"
        title={t("features.swimlane.saveFileAs")}
        message={t("features.swimlane.doYouwantSaveave")}
        buttonLabel={t("words.saveAs")}
        inputLabel={t("navigation.diagramName")}
        inputName="swimlane"
        inputValue={state.swimlaneName}
        inputRef={inputRef}
        handleChange={handleChange}
        handleAction={handleSave}
        submit={state.submit}
        alert={alert}
        closeModalRef={closeModal}
        showInput={true}
        isDisabled={state?.isDisabled}
        route="home"
      />
      <ConfirmationModal
        key={"checkSaveifSwapSwimlane"}
        id="checkSaveifSwapSwimlane"
        title={t("features.swimlane.saveFileAs")}
        message={t("features.swimlane.doYouwantSaveave")}
        buttonLabel={t("words.saveAs")}
        inputLabel={t("navigation.diagramName")}
        inputName="swimlane"
        inputValue={state.swimlaneName}
        inputRef={inputRef}
        handleChange={handleChange}
        handleAction={handleSave}
        submit={state.submit}
        alert={alert}
        closeModalRef={closeModal}
        showInput={true}
        isDisabled={state?.isDisabled}
        route="swilaneVersion"
        processId={processId}
        mapId={mapId}
      />

      <GenericModal
        sentence={t("features.swimlane.improvSentence")}
        processId={processId}
        title={t("words.improve")}
        action={() => {}}
        actionButton=""
        classNameSentence="text-white opacity-50"
      />

      <ApprovalModal swimlaneId={mapId} isDarkMode={false} />

      <LayerSettings
        block={layerSelection.block}
        line={layerSelection.line}
        startEnd={layerSelection.startEnd}
        onSelectionsChange={handleSelectionsChange}
        initialSelections={layer}
        t={t}
        swimlaneData={completeData}
        mapId={mapId}
        token={users.access_token}
      />

      <CollaboratorModal
        collaborators={collaborators}
        onToggleAccess={handleToggleAccess}
        onGrantAllAccess={handleGrantAllAccess}
        activeUsers={activeUsers}
      />

      <ShareModal
        swimlaneId={mapId}
        processId={processId}
        theme="light"
        linkBasePath={`${processId}/${mapId}`}
      />

      <AttributModal
        blockLayerAttributes={layerSelection.block}
        lineLayerAttributes={layerSelection.line}
        startEndLayerAttributes={layerSelection.startEnd}
        mapId={mapId}
        initialData={editableAttributes}
        onAttributesSaved={handleAttributesSaved}
      />

      {/* Tutorial Component */}
      <Tutorial
        run={runTutorial}
        steps={steps}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
      />

      {/* Floating Tutorial Button */}
      <FloatingTutorialButton
        onClick={startTutorial}
        customStyle={{ bottom: "80px" }}
      />

      {/* Notifications temps réel : participant rejoint / quitté */}
      <JoinNotification
        userJoinedEvent={userJoinedEvent}
        userLeftEvent={userLeftEvent}
      />
    </>
  );
}

export default App;
